/**
 * ACF Sessions List Page
 *
 * AI CodeForge sessions for a repository
 */

import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Bot, Loader2, AlertCircle } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@forgenetwork/ui';
import { SessionList } from '@/components/acf';
import { useCreateACFSession } from '@/hooks/useACF';
import { api } from '@/lib/api';
import { useToast } from '@/shared/hooks/useToast';

export const Route = createFileRoute('/_authenticated/repositories/$repoId/acf')({
  component: RepositoryACFPage,
});

interface RepositoryWithWorkspace {
  id: string;
  name: string;
  workspaceId: string;
}

function RepositoryACFPage(): React.JSX.Element {
  const { repoId } = Route.useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
  const [newSessionTitle, setNewSessionTitle] = React.useState('');

  // Fetch repository to get workspaceId
  const {
    data: repository,
    isLoading: repoLoading,
    error: repoError,
  } = useQuery<RepositoryWithWorkspace>({
    queryKey: ['repository', repoId],
    queryFn: async () => {
      return await api.get<RepositoryWithWorkspace>(`/repositories/${repoId}`);
    },
  });

  const createSession = useCreateACFSession();

  const handleBack = (): void => {
    navigate({
      to: '/repositories/$repoId',
      params: { repoId },
    });
  };

  const handleSelectSession = (sessionId: string): void => {
    navigate({
      to: '/repositories/$repoId/acf/$sessionId',
      params: { repoId, sessionId },
    });
  };

  const handleCreateNew = (): void => {
    setNewSessionTitle('');
    setCreateDialogOpen(true);
  };

  const handleCreateSession = async (): Promise<void> => {
    if (!repository?.workspaceId) {
      toast({
        title: 'Error',
        description: 'Workspace not found for this repository',
        variant: 'destructive',
      });
      return;
    }

    try {
      const session = await createSession.mutateAsync({
        workspaceId: repository.workspaceId,
        repositoryId: repoId,
        title: newSessionTitle || 'New AI Session',
      });

      toast({
        title: 'Session Created',
        description: 'Your AI coding session has been created.',
      });

      setCreateDialogOpen(false);

      // Navigate to the new session
      navigate({
        to: '/repositories/$repoId/acf/$sessionId',
        params: { repoId, sessionId: session.id },
      });
    } catch (error) {
      toast({
        title: 'Creation Failed',
        description: error instanceof Error ? error.message : 'Failed to create session',
        variant: 'destructive',
      });
    }
  };

  if (repoLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (repoError || !repository) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <AlertCircle className="h-8 w-8 text-destructive" />
              <p className="text-destructive">
                {repoError instanceof Error ? repoError.message : 'Repository not found'}
              </p>
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Repository
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">AI Coding Sessions</h1>
              <p className="text-muted-foreground mt-1">
                {repository.name} - AI-powered coding assistance
              </p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <Bot className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>AI CodeForge</CardTitle>
                <CardDescription>
                  Get AI assistance with your code. Ask questions, get suggestions,
                  and discuss your project with an AI coding assistant.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Sessions List */}
        <SessionList
          workspaceId={repository.workspaceId}
          onSelectSession={handleSelectSession}
          onCreateNew={handleCreateNew}
        />

        {/* Create Session Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New AI Coding Session</DialogTitle>
              <DialogDescription>
                Create a new AI coding session to get help with your code.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Session Title (optional)</Label>
                <Input
                  id="title"
                  value={newSessionTitle}
                  onChange={(e) => setNewSessionTitle(e.target.value)}
                  placeholder="e.g., Refactor authentication module"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                disabled={createSession.isPending}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateSession} disabled={createSession.isPending}>
                {createSession.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Session'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
